import { makeAutoObservable, runInAction } from 'mobx'

import { DISK_SEARCH_MIN_QUERY } from '../Contracts/DiskNode'
import { diskErrorText } from '../Helpers/diskErrors'
import { storageTitle } from '../Helpers/diskFormat'
import { diskFolderUrl, diskSharedUrl } from '../Helpers/diskRoutes'
import { nextSelection } from '../Helpers/diskSelection'
import { CopyDiskNodesTask } from '../Tasks/CopyDiskNodesTask'
import { CreateDiskFolderTask } from '../Tasks/CreateDiskFolderTask'
import { LoadDiskFolderItemsTask } from '../Tasks/LoadDiskFolderItemsTask'
import { LoadDiskNodeTask } from '../Tasks/LoadDiskNodeTask'
import { LoadDiskQuotaTask } from '../Tasks/LoadDiskQuotaTask'
import { LoadDiskRecentTask } from '../Tasks/LoadDiskRecentTask'
import { LoadDiskSharedWithMeTask } from '../Tasks/LoadDiskSharedWithMeTask'
import { LoadDiskStoragesTask } from '../Tasks/LoadDiskStoragesTask'
import { LoadDiskTrashTask } from '../Tasks/LoadDiskTrashTask'
import { MoveDiskNodesTask } from '../Tasks/MoveDiskNodesTask'
import { PurgeDiskNodeTask } from '../Tasks/PurgeDiskNodeTask'
import { RenameDiskNodeTask } from '../Tasks/RenameDiskNodeTask'
import { RestoreDiskNodesTask } from '../Tasks/RestoreDiskNodesTask'
import { SearchDiskNodesTask } from '../Tasks/SearchDiskNodesTask'
import { TrashDiskNodesTask } from '../Tasks/TrashDiskNodesTask'
import { DiskFolderTreeStore } from './DiskFolderTreeStore'
import { uploadQueueStore } from './UploadQueueStore'

import type {
    DiskLocation,
    DiskNode,
    DiskPermissions,
    DiskQuota,
    DiskSort,
    DiskStorage,
    DiskView,
} from '../Contracts/DiskNode'
import type { SelectionClick } from '../Helpers/diskSelection'

/** Ключ вигляду (список/плитка) у браузері — зручність одного глядача. */
const VIEW_STORAGE_KEY = 'disk.view'
const SEARCH_DELAY_MS = 350

export type DiskActionResult = { ok: true } | { ok: false; error: string }

export interface DiskCrumb {
    key: string
    title: string
    /** Папка, у яку веде крихта; `null` — корінь простору чи перелік «Спільних зі мною». */
    folderUuid: string | null
    /** Куди веде крихта. */
    url: string
    /** Папка, на яку можна кинути рядки; `null` — не папка («Спільні зі мною»). */
    dropTarget: string | null
}

/** Підпис розділу «Спільні зі мною» — у панелі, крихтах і заголовку. */
export const DISK_SHARED_TITLE = 'Спільні зі мною'

const readView = (): DiskView => {
    try {
        return window.localStorage.getItem(VIEW_STORAGE_KEY) === 'grid' ? 'grid' : 'list'
    } catch {
        return 'list'
    }
}

const writeView = (view: DiskView): void => {
    try {
        window.localStorage.setItem(VIEW_STORAGE_KEY, view)
    } catch {
        // Приватне вікно або заблоковане сховище: вигляд просто не запам'ятається
    }
}

/**
 * Стан екрана «Диск» (`DISK-001` §6): простори, відкрите місце (папка,
 * нещодавні, кошик), вміст, сортування, вигляд, вибір, пошук і модалки.
 * Читачі — `observer`.
 *
 * - Що відкрито, задає адреса: сторінка передає розібрані параметри в `open`.
 * - Відповідь, що запізнилася (людина встигла піти в іншу папку), відкидається
 *   за номером запиту й нічого не перетирає.
 * - Дії повертають `DiskActionResult`: текст відмови бека показує компонент
 *   (`App.useApp().message`), стор про UI не знає.
 */
export class DiskBrowserStore {
    storages: DiskStorage[] = []
    storagesLoaded = false
    storagesError: string | null = null
    quota: DiskQuota | null = null

    location: DiskLocation | null = null
    /** Картка відкритої папки: права, крихти. */
    folder: DiskNode | null = null
    items: DiskNode[] = []
    total = 0
    isLoading = false
    error: string | null = null

    sort: DiskSort = { field: 'name', order: 'asc' }
    view: DiskView = readView()
    selected: string[] = []
    anchor: string | null = null

    searchQuery = ''
    searchItems: DiskNode[] = []
    searchTotal = 0
    isSearching = false
    searchError: string | null = null

    isBusy = false

    nameModal: { mode: 'create' } | { mode: 'rename'; node: DiskNode } | null = null
    transfer: { mode: 'move' | 'copy'; nodes: DiskNode[] } | null = null

    /** Папка, над якою зараз тягнуть рядки, — для підсвітки цілі. */
    dropTarget: string | null = null

    readonly tree = new DiskFolderTreeStore()

    /** Що тягнуть: вибрані рядки, якщо потягнули один із них, інакше сам рядок. */
    private dragNodes: DiskNode[] = []

    private requestId = 0
    private searchRequestId = 0
    private searchTimer: ReturnType<typeof setTimeout> | null = null
    private storagesPromise: Promise<void> | null = null

    constructor() {
        makeAutoObservable<
            DiskBrowserStore,
            'requestId' | 'searchRequestId' | 'searchTimer' | 'storagesPromise' | 'dragNodes'
        >(this, {
            dragNodes: false,
            requestId: false,
            searchRequestId: false,
            searchTimer: false,
            storagesPromise: false,
            tree: false,
        })
    }

    // ---- Простори ---------------------------------------------------------

    get personalStorage(): DiskStorage | null {
        return this.storages.find((storage) => storage.type === 'personal') ?? null
    }

    get companyStorage(): DiskStorage | null {
        return this.storages.find((storage) => storage.type === 'company') ?? null
    }

    get departmentStorages(): DiskStorage[] {
        return this.storages.filter((storage) => storage.type === 'department')
    }

    get customStorages(): DiskStorage[] {
        return this.storages.filter((storage) => storage.type === 'custom')
    }

    storageByUuid(uuid: string | null | undefined): DiskStorage | null {
        return uuid ? (this.storages.find((storage) => storage.uuid === uuid) ?? null) : null
    }

    get currentStorage(): DiskStorage | null {
        const location = this.location

        return location === null || location.kind === 'recent' || location.kind === 'shared'
            ? null
            : this.storageByUuid(location.storageUuid)
    }

    /** «Спільні зі мною» — сам перелік (не папка всередині й не пошук). */
    get isSharedList(): boolean {
        return this.location?.kind === 'shared' && this.location.folderUuid === null && !this.isSearchActive
    }

    /** Простір, у кошик якого веде пункт «Кошик»: поточний або «Мій диск». */
    get trashStorage(): DiskStorage | null {
        return this.currentStorage ?? this.personalStorage ?? this.storages[0] ?? null
    }

    loadStorages(force = false): Promise<void> {
        if (!force && this.storagesPromise) return this.storagesPromise

        this.storagesPromise = (async () => {
            try {
                const storages = await LoadDiskStoragesTask()

                runInAction(() => {
                    this.storages = storages
                    this.storagesLoaded = true
                    this.storagesError = null
                })
            } catch (error) {
                runInAction(() => {
                    this.storagesLoaded = true
                    this.storagesError = diskErrorText(error, 'Не вдалося завантажити простори диска')
                })
                this.storagesPromise = null
            }
        })()

        return this.storagesPromise
    }

    async loadQuota(): Promise<void> {
        try {
            const quota = await LoadDiskQuotaTask()

            runInAction(() => {
                this.quota = quota
            })
        } catch {
            // Картка квоти просто не з'явиться — диском користуватися це не заважає
        }
    }

    // ---- Що відкрито ------------------------------------------------------

    /** Перехід за адресою. Той самий шлях повторно — лише перечитує вміст. */
    async open(location: DiskLocation): Promise<void> {
        const same = this.location !== null && JSON.stringify(this.location) === JSON.stringify(location)

        this.location = location

        if (!same) {
            this.clearSelection()
            this.clearSearch()
            this.items = []
            this.total = 0
            this.folder = null
        }

        await this.loadContents()
    }

    reload(): Promise<void> {
        return this.loadContents()
    }

    private async loadContents(): Promise<void> {
        const location = this.location
        if (location === null) return

        const id = ++this.requestId

        this.isLoading = true
        this.error = null

        try {
            await this.loadStorages()

            if (location.kind === 'recent') {
                const items = await LoadDiskRecentTask()

                runInAction(() => {
                    if (id !== this.requestId) return
                    this.folder = null
                    this.items = items
                    this.total = items.length
                })

                return
            }

            if (location.kind === 'shared') {
                await this.loadShared(location.folderUuid, id)

                return
            }

            const storage = this.storageByUuid(location.storageUuid)

            if (storage === null) {
                throw new Error('Простір не знайдено або у вас немає до нього доступу')
            }

            if (location.kind === 'trash') {
                const trash = await LoadDiskTrashTask(storage.uuid)

                runInAction(() => {
                    if (id !== this.requestId) return
                    this.folder = null
                    this.items = trash.items
                    this.total = trash.total
                })

                return
            }

            const folderUuid = location.folderUuid ?? storage.rootFolderUuid
            const [folder, items] = await Promise.all([
                LoadDiskNodeTask(folderUuid),
                LoadDiskFolderItemsTask({ parent: folderUuid, sort: this.sort }),
            ])

            runInAction(() => {
                if (id !== this.requestId) return
                this.folder = folder
                // Шлях до відкритої папки розгорнутий у лівій панелі
                this.tree.expand([
                    ...(folder.ancestors ?? []).map((ancestor) => ancestor.uuid),
                    ...(folder.uuid === storage.rootFolderUuid ? [folder.uuid] : []),
                ])
                this.items = items.items
                this.total = items.total
                // Вибрані рядки, яких більше немає (переміщені, у кошику), знімаємо
                this.selected = this.selected.filter((uuid) => items.items.some((item) => item.uuid === uuid))
            })
        } catch (error) {
            runInAction(() => {
                if (id !== this.requestId) return
                this.items = []
                this.total = 0
                this.folder = null
                this.error = diskErrorText(error, 'Не вдалося відкрити папку')
            })
        } finally {
            runInAction(() => {
                if (id === this.requestId) this.isLoading = false
            })
        }
    }

    /**
     * «Спільні зі мною»: перелік або папка всередині спільної. Простору тут
     * може й не бути (доступ лише на папці), тож папка відкривається напряму
     * за uuid, а крихти — від спільної папки (`ancestors` бек обрізає сам).
     */
    private async loadShared(folderUuid: string | null, id: number): Promise<void> {
        if (folderUuid === null) {
            const shared = await LoadDiskSharedWithMeTask()

            runInAction(() => {
                if (id !== this.requestId) return
                this.folder = null
                this.items = shared.items
                this.total = shared.total
                this.selected = this.selected.filter((uuid) => shared.items.some((item) => item.uuid === uuid))
            })

            return
        }

        const [folder, items] = await Promise.all([
            LoadDiskNodeTask(folderUuid),
            LoadDiskFolderItemsTask({ parent: folderUuid, sort: this.sort }),
        ])

        runInAction(() => {
            if (id !== this.requestId) return
            this.folder = folder
            this.items = items.items
            this.total = items.total
            this.selected = this.selected.filter((uuid) => items.items.some((item) => item.uuid === uuid))
        })
    }

    /** Uuid відкритої папки (корінь простору — його коренева папка). */
    get folderUuid(): string | null {
        const location = this.location

        if (location?.kind === 'shared') return location.folderUuid
        if (location?.kind !== 'folder') return null

        return location.folderUuid ?? this.storageByUuid(location.storageUuid)?.rootFolderUuid ?? null
    }

    get folderPermissions(): DiskPermissions {
        return this.folderUuid !== null && !this.isSearchActive ? (this.folder?.permissions ?? {}) : {}
    }

    /**
     * Крихти: простір → предки → поточна папка. У «Спільних зі мною» —
     * «Спільні зі мною» → предки від спільної папки → поточна: папок вище за
     * спільну людина без ролі простору не бачить (бек їх і не віддає).
     */
    get crumbs(): DiskCrumb[] {
        const location = this.location
        const folder = this.folder
        const crumb = (uuid: string, title: string, url: string): DiskCrumb => ({
            key: uuid,
            title,
            folderUuid: uuid,
            url,
            dropTarget: uuid,
        })

        if (location?.kind === 'shared') {
            const root: DiskCrumb = {
                key: 'shared',
                title: DISK_SHARED_TITLE,
                folderUuid: null,
                url: diskSharedUrl(),
                dropTarget: null,
            }

            if (location.folderUuid === null || folder === null) return [root]

            const ancestors = (folder.ancestors ?? [])
                .filter((ancestor) => !ancestor.root)
                .map((ancestor) => crumb(ancestor.uuid, ancestor.name, diskSharedUrl(ancestor.uuid)))

            return [root, ...ancestors, crumb(folder.uuid, folder.name, diskSharedUrl(folder.uuid))]
        }

        const storage = this.currentStorage

        if (storage === null || location?.kind !== 'folder') return []

        const root: DiskCrumb = {
            key: storage.rootFolderUuid,
            title: storageTitle(storage),
            folderUuid: null,
            url: diskFolderUrl(storage.uuid),
            dropTarget: storage.rootFolderUuid,
        }

        if (folder === null || folder.uuid === storage.rootFolderUuid || folder.parentUuid === null) return [root]

        const ancestors = (folder.ancestors ?? [])
            .filter((ancestor) => !ancestor.root)
            .map((ancestor) => crumb(ancestor.uuid, ancestor.name, diskFolderUrl(storage.uuid, ancestor.uuid)))

        return [root, ...ancestors, crumb(folder.uuid, folder.name, diskFolderUrl(storage.uuid, folder.uuid))]
    }

    // ---- Сортування й вигляд ---------------------------------------------

    setSort(sort: DiskSort): void {
        if (this.sort.field === sort.field && this.sort.order === sort.order) return

        this.sort = sort

        if (this.isSearchActive) {
            void this.runSearch()
        } else if (this.folderUuid !== null) {
            void this.loadContents()
        }
    }

    setView(view: DiskView): void {
        this.view = view
        writeView(view)
    }

    // ---- Вибір ------------------------------------------------------------

    /** Рядки, які зараз на екрані: результати пошуку або вміст. */
    get visibleItems(): DiskNode[] {
        return this.isSearchActive ? this.searchItems : this.items
    }

    get selectedNodes(): DiskNode[] {
        return this.visibleItems.filter((item) => this.selected.includes(item.uuid))
    }

    isSelected(uuid: string): boolean {
        return this.selected.includes(uuid)
    }

    click(uuid: string, modifiers: SelectionClick): void {
        const next = nextSelection(
            this.visibleItems.map((item) => item.uuid),
            { selected: this.selected, anchor: this.anchor },
            uuid,
            modifiers
        )

        this.selected = next.selected
        this.anchor = next.anchor
    }

    toggleSelected(uuid: string): void {
        this.click(uuid, { ctrl: true, shift: false })
    }

    setSelected(uuids: string[]): void {
        this.selected = uuids
        this.anchor = uuids[uuids.length - 1] ?? null
    }

    clearSelection(): void {
        this.selected = []
        this.anchor = null
    }

    // ---- Пошук ------------------------------------------------------------

    get isSearchActive(): boolean {
        return this.searchQuery.trim().length >= DISK_SEARCH_MIN_QUERY
    }

    setSearchQuery(query: string): void {
        this.searchQuery = query
        this.clearSelection()

        if (this.searchTimer !== null) clearTimeout(this.searchTimer)

        if (!this.isSearchActive) {
            this.searchRequestId += 1
            this.searchItems = []
            this.searchTotal = 0
            this.searchError = null
            this.isSearching = false

            return
        }

        this.isSearching = true
        this.searchTimer = setTimeout(() => void this.runSearch(), SEARCH_DELAY_MS)
    }

    clearSearch(): void {
        if (this.searchQuery !== '') this.setSearchQuery('')
    }

    private async runSearch(): Promise<void> {
        const query = this.searchQuery.trim()
        if (query.length < DISK_SEARCH_MIN_QUERY) return

        const id = ++this.searchRequestId

        this.isSearching = true
        this.searchError = null

        try {
            const result = await SearchDiskNodesTask(query, this.sort)

            runInAction(() => {
                if (id !== this.searchRequestId) return
                this.searchItems = result.items
                this.searchTotal = result.total
            })
        } catch (error) {
            runInAction(() => {
                if (id !== this.searchRequestId) return
                this.searchItems = []
                this.searchTotal = 0
                this.searchError = diskErrorText(error, 'Не вдалося виконати пошук')
            })
        } finally {
            runInAction(() => {
                if (id === this.searchRequestId) this.isSearching = false
            })
        }
    }

    // ---- Модалки ----------------------------------------------------------

    openCreateFolder(): void {
        this.nameModal = { mode: 'create' }
    }

    openRename(node: DiskNode): void {
        this.nameModal = { mode: 'rename', node }
    }

    closeNameModal(): void {
        this.nameModal = null
    }

    openTransfer(mode: 'move' | 'copy', nodes: DiskNode[]): void {
        if (nodes.length > 0) this.transfer = { mode, nodes }
    }

    closeTransfer(): void {
        this.transfer = null
    }

    // ---- Перетягування рядків на папку = переміщення ---------------------

    startDrag(node: DiskNode): void {
        this.dragNodes = this.isSelected(node.uuid) ? this.selectedNodes : [node]
    }

    endDrag(): void {
        this.dragNodes = []
        this.dropTarget = null
    }

    /** Чи можна кинути на цю папку: тягнуть щось, і це не сама папка. */
    canDropOn(folderUuid: string): boolean {
        return this.dragNodes.length > 0 && !this.dragNodes.some((node) => node.uuid === folderUuid)
    }

    setDropTarget(folderUuid: string | null): void {
        if (this.dropTarget !== folderUuid) this.dropTarget = folderUuid
    }

    /** Кинули на папку: переміщуємо те, що тягнули. */
    dropOn(folderUuid: string): Promise<DiskActionResult> | null {
        const nodes = this.dragNodes

        this.endDrag()

        if (nodes.length === 0 || nodes.some((node) => node.uuid === folderUuid)) return null
        if (nodes.every((node) => node.parentUuid === folderUuid)) return null

        return this.move(nodes, folderUuid)
    }

    // ---- Дії --------------------------------------------------------------

    private async run(action: () => Promise<void>, fallback: string): Promise<DiskActionResult> {
        this.isBusy = true

        try {
            await action()

            return { ok: true }
        } catch (error) {
            return { ok: false, error: diskErrorText(error, fallback) }
        } finally {
            runInAction(() => {
                this.isBusy = false
            })
        }
    }

    async createFolder(name: string): Promise<DiskActionResult> {
        const parent = this.folderUuid
        if (parent === null) return { ok: false, error: 'Відкрийте папку, у якій створити нову' }

        const result = await this.run(
            () => CreateDiskFolderTask(parent, name).then(() => undefined),
            'Не вдалося створити папку'
        )

        if (result.ok) {
            this.tree.invalidate([parent])
            await this.reload()
        }

        return result
    }

    async rename(node: DiskNode, name: string): Promise<DiskActionResult> {
        const result = await this.run(
            () => RenameDiskNodeTask(node.uuid, name).then(() => undefined),
            'Не вдалося перейменувати'
        )

        if (result.ok) {
            if (node.type === 'folder') this.tree.invalidate([node.parentUuid])
            await this.refreshVisible()
        }

        return result
    }

    async move(nodes: DiskNode[], target: string): Promise<DiskActionResult> {
        const uuids = nodes.map((node) => node.uuid).filter((uuid) => uuid !== target)
        if (uuids.length === 0) return { ok: false, error: 'Папку не можна перемістити саму в себе' }

        const result = await this.run(() => MoveDiskNodesTask(uuids, target), 'Не вдалося перемістити')

        if (result.ok) {
            this.tree.invalidate([
                target,
                ...nodes.filter((node) => node.type === 'folder').map((node) => node.parentUuid),
            ])
            this.clearSelection()
            await Promise.all([this.refreshVisible(), this.loadQuota()])
        }

        return result
    }

    async copy(nodes: DiskNode[], target: string): Promise<DiskActionResult> {
        const result = await this.run(
            () =>
                CopyDiskNodesTask(
                    nodes.map((node) => node.uuid),
                    target
                ),
            'Не вдалося скопіювати'
        )

        if (result.ok) {
            this.tree.invalidate([target])
            await Promise.all([this.refreshVisible(), this.loadQuota()])
        }

        return result
    }

    async trash(nodes: DiskNode[]): Promise<DiskActionResult> {
        const result = await this.run(
            () => TrashDiskNodesTask(nodes.map((node) => node.uuid)),
            'Не вдалося перемістити в кошик'
        )

        if (result.ok) {
            this.tree.invalidate(nodes.filter((node) => node.type === 'folder').map((node) => node.parentUuid))
            this.clearSelection()
            await this.refreshVisible()
        }

        return result
    }

    async restore(nodes: DiskNode[]): Promise<DiskActionResult> {
        const result = await this.run(
            () => RestoreDiskNodesTask(nodes.map((node) => node.uuid)),
            'Не вдалося відновити'
        )

        if (result.ok) {
            this.tree.invalidate(nodes.map((node) => node.parentUuid))
            this.clearSelection()
            await this.reload()
        }

        return result
    }

    /** Видалити назавжди — по одному: бек приймає один корінь кошика за запит. */
    async purge(nodes: DiskNode[]): Promise<DiskActionResult> {
        const result = await this.run(async () => {
            for (const node of nodes) await PurgeDiskNodeTask(node.uuid)
        }, 'Не вдалося видалити назавжди')

        this.clearSelection()
        await Promise.all([this.reload(), this.loadQuota()])

        return result
    }

    /**
     * Менеджер завантажень завершив серію файлів: якщо людина в одній із цих
     * папок — перечитати вміст; дерево — коли з'явилися нові папки; квоту —
     * завжди (картка «використано X з Y»).
     */
    async refreshAfterUpload(parents: string[], withFolders: boolean): Promise<void> {
        if (withFolders) this.tree.invalidate(parents)

        const folder = this.folderUuid
        const showing = folder !== null && parents.includes(folder) && !this.isSearchActive

        await Promise.all([showing ? this.reload() : Promise.resolve(), this.loadQuota()])
    }

    /** Після дії над рядками: перечитати те, що на екрані (пошук чи вміст). */
    refreshVisible(): Promise<void> {
        return this.isSearchActive ? this.runSearch() : this.reload()
    }
}

export const diskBrowserStore = new DiskBrowserStore()

// Черга завантажень глобальна й про екран не знає: оновлення папки — підпискою
uploadQueueStore.onUploaded(({ parents, withFolders }) => void diskBrowserStore.refreshAfterUpload(parents, withFolders))
